import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { BodyWrapper, Header, Loader, MainWrapper, Message } from '@components';
import { datamodelIdFromType } from '@helpers';
import {
  datamodelQuery,
  datamodelsQuery,
  siteMutation,
  siteQuery,
  themeQuery,
} from '@queries';

import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { ManageContentForm } from './ManageContentForm/ManageContentForm.jsx';
import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import { useState, useEffect, useRef } from 'react';
import { errorsFromMutate } from '@Forms/utils.js';

export const SiteManageContent = () => {
  const { t, i18n } = useTranslation();
  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);

  // scroll to error message when it's set
  useEffect(() => {
    if (errorMessage && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [errorMessage]);

  // query client for triggering invalidations
  const queryClient = useQueryClient();
  const { siteID } = useParams();

  // Fetches site details and custom field values
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(
      siteID,
      {
        staleTime: Infinity,
      },
      {
        with_draft: true,
      },
    ),
  );

  // fetch theme
  const { data: themeData, isPending: themeIsPending } = useQuery(
    themeQuery(siteData?.theme_id, {
      enabled: !!siteData?.theme_id,
    }),
  );

  // get the datamodels list
  const { data: datamodelsData, isPending: datamodelsIsPending } = useQuery(
    datamodelsQuery({
      enabled: !themeIsPending,
    }),
  );

  // get the datamodel ID for site custom fields
  const datamodelID = useMemo(() => {
    return themeData?.datamodel_id
      ? themeData.datamodel_id
      : datamodelsData?.objects
        ? datamodelIdFromType(datamodelsData?.objects, 'site_custom_fields')
        : null;
  }, [themeData, datamodelsData]);

  // get the datamodel
  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(datamodelID, {
      enabled: !!datamodelID,
    }),
  );

  // get all the related datamodels used on fields
  const relatedDatamodels = useQueries({
    enabled: !datamodelIsPending,
    queries: datamodelData
      ? datamodelData.fields
          .filter((field) => Number.isInteger(field.related_datamodel_id))
          .map((field) => datamodelQuery(field.related_datamodel_id))
      : [],
  });
  // are all datamodel queries finished
  const allFinished = relatedDatamodels.every((query) => query.isSuccess);

  const onMutationError = (error) => {
    if (!error) {
      return;
    }
    const errorsList = errorsFromMutate(error);
    const allError = errorsList.find(({ field }) => field === '__all__');
    if (!!allError) {
      setErrorMessage(allError.error.message);
    }
  };

  // saves the current changes as a draft
  const mutation = useMutation(
    siteMutation(siteID, {
      // on trigger
      onMutate: (data) => {
        setErrorMessage(null);
      },
      onSuccess: () => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
      },
      onError: onMutationError,
    }),
  );

  // publishes the current changes (or the pending draft) live
  const publishMutation = useMutation(
    siteMutation(siteID, {
      // on trigger
      onMutate: (data) => {
        setErrorMessage(null);
      },
      onSuccess: () => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
      },
      onError: onMutationError,
    }),
  );

  const isAllPending =
    datamodelIsPending || datamodelsIsPending || siteIsPending || !allFinished;

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            {/* New */}
            <SiteBreadcrumbs siteData={siteData}>
              <li className="sb-flex">
                <span>{t('Manage content')}</span>
              </li>
            </SiteBreadcrumbs>
          </div>
          <div className="sb-manage-content">
            <h1>{t('Manage content')}</h1>
            {errorMessage && (
              <div ref={errorMessageRef}>
                <Message
                  closable={true}
                  messageStyle="error"
                  title={'Something Went Wrong'}
                  isFormMessage={false}
                  message={errorMessage}
                />
              </div>
            )}
            {isAllPending && <Loader className="sb-loader sb-loader-large" />}
            {siteData && datamodelData && datamodelsData && allFinished && (
              <ManageContentForm
                datamodelData={datamodelData}
                mutation={mutation}
                publishMutation={publishMutation}
                disabled={mutation.isPending || publishMutation.isPending}
                relatedDatamodels={relatedDatamodels}
                siteData={siteData}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
